import React, { ChangeEvent, FC, useCallback, useMemo } from "react"
import { ExclusiveChoiceDropdown, SingleLineTextInput } from "dash-styled"

import {
    MetricConstraint,
    MetricConstraintOperator
} from "../../../../../../../../../../../../Models/DataProgram/LabelerRestriction"
import {
    FetchState,
    hasValue,
    hasResult
} from "../../../../../../../../../../../../Models/Client/FetchState"

interface QualificationMetricChooserProps {
    choicesFetch: FetchState<string[]>
    // for an interface reason, this component receives an array as value but its length is supposed to not exceed 1.
    value: MetricConstraint[] | undefined
    onChange: (value: MetricConstraint[] | undefined) => void
    onSearchChoices: (search: string) => void
    onLoadMoreChoices?: () => void
}

const operators: [string, MetricConstraintOperator][] = [
    ["<", "LESS_THAN"],
    [">", "GREATER_THAN"],
    ["≥", "GREATER_THAN_OR_EQUALS_TO"],
    ["≤", "LESS_THAN_OR_EQUALS_TO"],
    ["=", "EQUALS_TO"],
    ["exists", "EXISTS"],
    ["does not exist", "NOT_EXISTS"]
]

const operatorLabels = operators.map(([label]) => label)

export const QualificationMetricChooser: FC<QualificationMetricChooserProps> =
    ({ choicesFetch, value, onChange, onSearchChoices, onLoadMoreChoices }) => {
        const choices = useMemo(
            () =>
                hasValue(choicesFetch)
                    ? choicesFetch.value.filter(
                          (choice, i, arr) => arr.indexOf(choice) === i
                      )
                    : [],
            [choicesFetch]
        )
        const handleChangeName = useCallback(
            (choice: string) => {
                if (choices !== undefined) {
                    onChange(value?.map(v => ({ ...v, name: choice })))
                }
            },
            [choices, onChange, value]
        )
        const handleChangeOperator = useCallback(
            (index: number) =>
                onChange(
                    value?.map(v => ({ ...v, operator: operators[index][1] }))
                ),
            [onChange, value]
        )
        const handleChangeValue = useCallback(
            (e: ChangeEvent<HTMLInputElement>) =>
                onChange(
                    value?.map(v => ({
                        ...v,
                        value: parseFloat(e.target.value)
                    }))
                ),
            [onChange, value]
        )
        const shouldShowValue =
            value !== undefined &&
            value.length > 0 &&
            value[0].operator !== "EXISTS" &&
            value[0].operator !== "NOT_EXISTS"

        return (
            <div className="flex-grow flex flex-col items-stretch">
                <ExclusiveChoiceDropdown
                    className="w-full flex-grow flex-shrink mb-1"
                    testId="qualifier-metric-chooser"
                    size="sm"
                    choices={choices}
                    value={
                        value === undefined || value.length === 0
                            ? undefined
                            : value[0].name
                    }
                    isLoadingChoices={!hasResult(choicesFetch)}
                    onClick={handleChangeName}
                    onSearch={onSearchChoices}
                    onReachedToEndOfChoices={onLoadMoreChoices}
                />
                <div className="flex items-center">
                    <span className="mr-2 whitespace-no-wrap">with score</span>
                    <ExclusiveChoiceDropdown
                        className="w-1/2 flex-grow flex-shrink"
                        testId="qualifier-operator-chooser"
                        size="sm"
                        choices={operatorLabels}
                        selectedIndex={
                            operators.findIndex(
                                ([, op]) => op === value?.[0].operator
                            ) ?? -1
                        }
                        onClick={handleChangeOperator}
                    />
                    {shouldShowValue ? (
                        <SingleLineTextInput
                            value={
                                value === undefined || value.length === 0
                                    ? undefined
                                    : String(value[0].value)
                            }
                            className="ml-2 w-1/2 flex-grow flex-shrink"
                            testId="qualifier-value-chooser"
                            type="number"
                            transparentBg={true}
                            size="sm"
                            onChange={handleChangeValue}
                        />
                    ) : null}
                </div>
            </div>
        )
    }
